import PropTypes from "prop-types";
import {
  convertToTitleCase,
  getDateWithFormat,
  prettyColumnName,
} from "../../../utils/commons";
import "./commonTableView.scss";

const getRowKey = (row) =>
  row?.id || row?.codeGroupId || row?.key || row?.slug || JSON.stringify(row);

const getNestedValue = (obj, path) => {
  if (!path || !obj) return undefined;
  return path.split(".").reduce((current, key) => current?.[key], obj);
};

const renderStatusBadge = (value) => {
  const statusClass =
    value && typeof value === "string"
      ? value.toLowerCase().replace(/[\s_]/g, "-")
      : "default";

  return (
    <div className="performances-widget-status-type">
      <span className={`performances-lifecycle-status ${statusClass}`}>
        {value?.replace(/_/g, " ") || "N/A"}
      </span>
    </div>
  );
};

const renderArrayJoin = (value, columnConfigEntry, colKey) => {
  if (!Array.isArray(value)) {
    return value != null ? String(value) : "N/A";
  }

  const joinField = columnConfigEntry.joinField || null;
  if (!joinField) {
    return value.join(", ") || "N/A";
  }

  const isOutcomeField = joinField === "outcome" || colKey === "outcomeName";

  const mapFunction = isOutcomeField
    ? (item) => convertToTitleCase(item[joinField] ?? "N/A")
    : (item) => item[joinField] ?? "N/A";

  return value.map(mapFunction).join(", ") || "N/A";
};

const renderDefaultValue = (value) => {
  if (Array.isArray(value) && value.length === 0) {
    return "N/A";
  }
  return value != null ? String(value) : "N/A";
};

const getFlattenedFieldValue = (items, fieldKey) => {
  const mappedValues = items.map((item) => {
    const fieldValue = item[fieldKey] || "N/A";
    return fieldKey === "outcome" ? convertToTitleCase(fieldValue) : fieldValue;
  });

  return mappedValues.join(", ") || "N/A";
};

const applyFlattenedConfig = (newRow, row, key, config) => {
  if (!config?.flatten || !Array.isArray(row[key]) || row[key].length === 0) {
    return;
  }

  if (config.joinArrayValues) {
    config.fields.forEach((field) => {
      newRow[`${key}_${field.key}`] = getFlattenedFieldValue(row[key], field.key);
    });
    return;
  }

  const flattenedItem = row[key][0];
  config.fields.forEach((field) => {
    newRow[`${key}_${field.key}`] = flattenedItem[field.key];
  });
};

const applyValuePathConfig = (newRow, row, key, config) => {
  if (config?.valuePath && !Object.hasOwn(newRow, key)) {
    newRow[key] = getNestedValue(row, config.valuePath);
  }
};

const transformRow = (row, columnConfig) => {
  const newRow = { ...row };

  Object.keys(columnConfig).forEach((key) => {
    const config = columnConfig[key];
    applyFlattenedConfig(newRow, row, key, config);
    applyValuePathConfig(newRow, row, key, config);
  });

  return newRow;
};

const transformContentList = (contentList, columnConfig) =>
  contentList.map((row) => transformRow(row, columnConfig));

const renderCellValue = (colKey, value, columnConfigEntry, programTimezone) => {
  // If a fully custom render is defined, use that
  if (typeof columnConfigEntry?.render === "function") {
    return columnConfigEntry.render(value);
  }

  const { type } = columnConfigEntry || {};

  switch (type) {
    case "statusBadge":
      return renderStatusBadge(value);
    case "date":
      return value ? getDateWithFormat(value, programTimezone) : "N/A";
    case "arrayJoin":
      return renderArrayJoin(value, columnConfigEntry, colKey);
    default:
      return renderDefaultValue(value);
  }
};

const renderClickableCell = (colKey, cellContent, cellClickHandlers, row) => {
  if (!cellClickHandlers[colKey]) {
    return cellContent;
  }

  return (
    <button
      type="button"
      className="common-table-cell-button"
      onClick={() => cellClickHandlers[colKey](row)}
    >
      {cellContent}
    </button>
  );
};

const getColumnLabel = (configEntry, colKey) =>
  configEntry?.label || prettyColumnName(colKey);

const getColumnConfigEntry = (colKey, columnConfig) => {
  if (columnConfig[colKey]) {
    return columnConfig[colKey];
  }

  for (const key in columnConfig) {
    if (columnConfig[key]?.flatten) {
      const fieldConfig = columnConfig[key].fields.find(
        (field) => `${key}_${field.key}` === colKey
      );
      if (fieldConfig) {
        return { label: fieldConfig.label };
      }
    }
  }

  return null;
};

const CommonTableView = ({
  contentList = [],
  columnConfig = {},
  programTimezone,
  excludeColumns = [],
  cellClickHandlers = {},
  noResultsFound = null,
  noDataLabel = "No data available",
}) => {
  if (!contentList.length && !noResultsFound) {
    return <p>No data available</p>;
  }

  const transformedContentList = transformContentList(contentList, columnConfig);

  // Get all unique column keys from the transformed data and filter out excluded columns
  const columns = Array.from(
    new Set(transformedContentList.flatMap((row) => Object.keys(row)))
  ).filter((colKey) => !excludeColumns.includes(colKey));

  return (
    <div className="performances-container">
      <div className="performances-list-container">
        <table className="performances-list-table">
          <thead>
            <tr>
              {columns.map((colKey) => {
                const configEntry = getColumnConfigEntry(colKey, columnConfig);
                const label = getColumnLabel(configEntry, colKey);
                const customClass = configEntry?.headerClass;
                return (
                  <th
                    key={colKey}
                    className={
                      customClass ? `performances-column-${customClass}` : ""
                    }
                  >
                    {label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {transformedContentList.length ? (
              transformedContentList.map((row) => (
                <tr key={getRowKey(row)}>
                  {columns.map((colKey) => {
                    const rawValue = row[colKey];
                    const configEntry = getColumnConfigEntry(colKey, columnConfig);
                    const cellContent = renderCellValue(
                      colKey,
                      rawValue,
                      configEntry,
                      programTimezone
                    );
                    return (
                      <td key={colKey}>
                        {renderClickableCell(
                          colKey,
                          cellContent,
                          cellClickHandlers,
                          row
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                {noResultsFound != null ? null : (
                  <td colSpan={columns.length}>{noDataLabel}</td>
                )}
              </tr>
            )}
          </tbody>
        </table>
        {noResultsFound && transformedContentList?.length === 0 && noResultsFound}
      </div>
    </div>
  );
};
CommonTableView.propTypes = {
  contentList: PropTypes.array.isRequired,
  columnConfig: PropTypes.object,
  programTimezone: PropTypes.string,
  excludeColumns: PropTypes.array,
  cellClickHandlers: PropTypes.object,
  noResultsFound: PropTypes.node,
  noDataLabel: PropTypes.string,
};

export default CommonTableView;
